<script setup>
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  generateTranslationPayload,
  generateLogActionKey,
} from 'dashboard/helper/auditlogHelper';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const records = computed(() => getters['auditlogs/getAuditLogs'].value);
const uiFlags = computed(() => getters['auditlogs/getUIFlags'].value);
const meta = computed(() => getters['auditlogs/getMeta'].value);
const agentList = computed(() => getters['agents/getAgents'].value);

const { t } = useI18n();
const route = useRoute();

const routerPage = computed(() => Number(route.query.page ?? 1));

// Filter state
const filterUserId = ref('');
const filterAuditableType = ref('');
const filterDateFrom = ref('');
const filterDateTo = ref('');
const expandedLogId = ref(null);

const auditableTypes = [
  { value: '', label: 'AUDIT_LOGS.FILTERS.ALL_TYPES' },
  { value: 'Account', label: 'AUDIT_LOGS.FILTERS.TYPE_ACCOUNT' },
  { value: 'AccountUser', label: 'AUDIT_LOGS.FILTERS.TYPE_ACCOUNT_USER' },
  { value: 'Inbox', label: 'AUDIT_LOGS.FILTERS.TYPE_INBOX' },
  { value: 'InboxMember', label: 'AUDIT_LOGS.FILTERS.TYPE_INBOX_MEMBER' },
  { value: 'Team', label: 'AUDIT_LOGS.FILTERS.TYPE_TEAM' },
  { value: 'TeamMember', label: 'AUDIT_LOGS.FILTERS.TYPE_TEAM_MEMBER' },
  { value: 'Webhook', label: 'AUDIT_LOGS.FILTERS.TYPE_WEBHOOK' },
  { value: 'AutomationRule', label: 'AUDIT_LOGS.FILTERS.TYPE_AUTOMATION' },
  { value: 'Macro', label: 'AUDIT_LOGS.FILTERS.TYPE_MACRO' },
  { value: 'Conversation', label: 'AUDIT_LOGS.FILTERS.TYPE_CONVERSATION' },
  { value: 'User', label: 'AUDIT_LOGS.FILTERS.TYPE_USER' },
];

const fetchAuditLogs = page => {
  try {
    store.dispatch('auditlogs/fetch', {
      page,
      userId: filterUserId.value || undefined,
      auditableType: filterAuditableType.value || undefined,
      from: filterDateFrom.value || undefined,
      to: filterDateTo.value || undefined,
    });
  } catch (error) {
    const errorMessage = error?.message || t('AUDIT_LOGS.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const generateLogText = auditLogItem => {
  const payload = generateTranslationPayload(auditLogItem, agentList.value);
  const translationKey = generateLogActionKey(auditLogItem);

  const joinIfArray = value => {
    return Array.isArray(value) ? value.join(', ') : value;
  };

  const mergedPayload = {
    ...payload,
    attributes: joinIfArray(payload.attributes),
    values: joinIfArray(payload.values),
  };
  return t(translationKey, mergedPayload);
};

const onPageChange = page => {
  router.push({ name: 'auditlogs_list', query: { page: page } });
};

const applyFilters = () => {
  router.push({ name: 'auditlogs_list', query: { page: 1 } });
  fetchAuditLogs(1);
};

const clearFilters = () => {
  filterUserId.value = '';
  filterAuditableType.value = '';
  filterDateFrom.value = '';
  filterDateTo.value = '';
  applyFilters();
};

const toggleExpand = logId => {
  expandedLogId.value = expandedLogId.value === logId ? null : logId;
};

const formatFieldName = field => {
  return field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const formatValue = value => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchAuditLogs(routerPage.value);
});

watch(routerPage, (newPage, oldPage) => {
  if (newPage !== oldPage) {
    fetchAuditLogs(newPage);
  }
});

const tableHeaders = computed(() => {
  return [
    t('AUDIT_LOGS.LIST.TABLE_HEADER.ACTIVITY'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.TIME'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.IP_ADDRESS'),
    '',
  ];
});
</script>

<template>
  <div class="flex-1 overflow-auto">
    <BaseSettingsHeader
      :title="$t('AUDIT_LOGS.HEADER')"
      :description="$t('AUDIT_LOGS.DESCRIPTION')"
      :link-text="$t('AUDIT_LOGS.LEARN_MORE')"
      feature-name="audit_logs"
    />

    <!-- Filters -->
    <div class="mt-4 p-4 bg-n-solid-2 rounded-lg border border-n-weak">
      <h3 class="text-sm font-semibold text-n-slate-12 mb-3">
        {{ $t('AUDIT_LOGS.FILTERS.TITLE') }}
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label class="text-xs text-n-slate-11 mb-1 block">
            {{ $t('AUDIT_LOGS.FILTERS.USER') }}
          </label>
          <select
            v-model="filterUserId"
            class="!mb-0 text-sm w-full"
          >
            <option value="">
              {{ $t('AUDIT_LOGS.FILTERS.ALL_USERS') }}
            </option>
            <option
              v-for="agent in agentList"
              :key="agent.id"
              :value="agent.id"
            >
              {{ agent.name }}
            </option>
          </select>
        </div>
        <div>
          <label class="text-xs text-n-slate-11 mb-1 block">
            {{ $t('AUDIT_LOGS.FILTERS.TYPE') }}
          </label>
          <select
            v-model="filterAuditableType"
            class="!mb-0 text-sm w-full"
          >
            <option
              v-for="type in auditableTypes"
              :key="type.value"
              :value="type.value"
            >
              {{ $t(type.label) }}
            </option>
          </select>
        </div>
        <div>
          <label class="text-xs text-n-slate-11 mb-1 block">
            {{ $t('AUDIT_LOGS.FILTERS.DATE_FROM') }}
          </label>
          <input
            v-model="filterDateFrom"
            type="date"
            class="!mb-0 text-sm w-full"
          />
        </div>
        <div>
          <label class="text-xs text-n-slate-11 mb-1 block">
            {{ $t('AUDIT_LOGS.FILTERS.DATE_TO') }}
          </label>
          <input
            v-model="filterDateTo"
            type="date"
            class="!mb-0 text-sm w-full"
          />
        </div>
      </div>
      <div class="flex gap-2 mt-3">
        <button
          class="px-3 py-1.5 text-sm bg-woot-500 text-white rounded-md hover:bg-woot-600"
          @click="applyFilters"
        >
          {{ $t('AUDIT_LOGS.FILTERS.APPLY') }}
        </button>
        <button
          class="px-3 py-1.5 text-sm bg-n-solid-3 text-n-slate-11 rounded-md hover:bg-n-solid-4"
          @click="clearFilters"
        >
          {{ $t('AUDIT_LOGS.FILTERS.CLEAR') }}
        </button>
      </div>
    </div>

    <div class="mt-6 flex-1 text-n-slate-11">
      <woot-loading-state
        v-if="uiFlags.fetchingList"
        :message="$t('AUDIT_LOGS.LOADING')"
      />
      <p
        v-else-if="!records.length"
        class="flex flex-col items-center justify-center h-full text-base p-8"
      >
        {{ $t('AUDIT_LOGS.LIST.404') }}
      </p>
      <div v-else class="min-w-full overflow-x-auto">
        <table class="divide-y divide-n-weak w-full">
          <thead>
            <th
              v-for="thHeader in tableHeaders"
              :key="thHeader"
              class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11"
            >
              {{ thHeader }}
            </th>
          </thead>
          <tbody class="divide-y divide-n-weak text-n-slate-11">
            <template v-for="auditLogItem in records" :key="auditLogItem.id">
              <tr
                class="cursor-pointer hover:bg-n-solid-2"
                @click="toggleExpand(auditLogItem.id)"
              >
                <td class="py-4 ltr:pr-4 rtl:pl-4 break-all">
                  <div class="flex items-center gap-2">
                    <span
                      class="inline-block px-1.5 py-0.5 text-xs rounded font-medium"
                      :class="{
                        'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200': auditLogItem.action === 'create',
                        'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200': auditLogItem.action === 'update',
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200': auditLogItem.action === 'destroy',
                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200': !['create', 'update', 'destroy'].includes(auditLogItem.action),
                      }"
                    >
                      {{ auditLogItem.action }}
                    </span>
                    <span>{{ generateLogText(auditLogItem) }}</span>
                  </div>
                </td>
                <td class="py-4 ltr:pr-4 rtl:pl-4 whitespace-nowrap">
                  {{
                    messageTimestamp(
                      auditLogItem.created_at,
                      'MMM dd, yyyy hh:mm a'
                    )
                  }}
                </td>
                <td class="py-4 ltr:pr-4 rtl:pl-4 w-[8.75rem]">
                  {{ auditLogItem.remote_address }}
                </td>
                <td class="py-4 w-8 text-center">
                  <span
                    class="text-n-slate-10 transition-transform inline-block"
                    :class="{ 'rotate-180': expandedLogId === auditLogItem.id }"
                  >
                    ▼
                  </span>
                </td>
              </tr>
              <!-- Expanded detail row -->
              <tr v-if="expandedLogId === auditLogItem.id">
                <td colspan="4" class="p-4 bg-n-solid-2">
                  <div class="grid gap-3">
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div>
                        <span class="font-semibold text-n-slate-12">
                          {{ $t('AUDIT_LOGS.DETAILS.TYPE') }}
                        </span>
                        <p>{{ auditLogItem.auditable_type }}</p>
                      </div>
                      <div>
                        <span class="font-semibold text-n-slate-12">
                          {{ $t('AUDIT_LOGS.DETAILS.ID') }}
                        </span>
                        <p>#{{ auditLogItem.auditable_id }}</p>
                      </div>
                      <div>
                        <span class="font-semibold text-n-slate-12">
                          {{ $t('AUDIT_LOGS.DETAILS.USER') }}
                        </span>
                        <p>{{ auditLogItem.username || '—' }}</p>
                      </div>
                      <div>
                        <span class="font-semibold text-n-slate-12">
                          {{ $t('AUDIT_LOGS.DETAILS.ACTION') }}
                        </span>
                        <p>{{ auditLogItem.action }}</p>
                      </div>
                    </div>

                    <!-- Change details table -->
                    <div
                      v-if="auditLogItem.change_details && auditLogItem.change_details.length"
                    >
                      <h4 class="text-xs font-semibold text-n-slate-12 mb-2">
                        {{ $t('AUDIT_LOGS.DETAILS.CHANGES') }}
                      </h4>
                      <table class="w-full text-xs border border-n-weak rounded">
                        <thead>
                          <tr class="bg-n-solid-3">
                            <th class="p-2 text-left font-semibold">
                              {{ $t('AUDIT_LOGS.DETAILS.FIELD') }}
                            </th>
                            <th class="p-2 text-left font-semibold">
                              {{ $t('AUDIT_LOGS.DETAILS.OLD_VALUE') }}
                            </th>
                            <th class="p-2 text-left font-semibold">
                              {{ $t('AUDIT_LOGS.DETAILS.NEW_VALUE') }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(change, idx) in auditLogItem.change_details"
                            :key="idx"
                            class="border-t border-n-weak"
                          >
                            <td class="p-2 font-medium">
                              {{ formatFieldName(change.field) }}
                            </td>
                            <td class="p-2 text-red-600 dark:text-red-400">
                              {{ change.old_value !== undefined ? formatValue(change.old_value) : '—' }}
                            </td>
                            <td class="p-2 text-green-600 dark:text-green-400">
                              {{ change.new_value !== undefined ? formatValue(change.new_value) : formatValue(change.value) }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Raw audited_changes fallback -->
                    <div
                      v-else-if="auditLogItem.audited_changes && Object.keys(auditLogItem.audited_changes).length"
                    >
                      <h4 class="text-xs font-semibold text-n-slate-12 mb-2">
                        {{ $t('AUDIT_LOGS.DETAILS.CHANGES') }}
                      </h4>
                      <table class="w-full text-xs border border-n-weak rounded">
                        <thead>
                          <tr class="bg-n-solid-3">
                            <th class="p-2 text-left font-semibold">
                              {{ $t('AUDIT_LOGS.DETAILS.FIELD') }}
                            </th>
                            <th class="p-2 text-left font-semibold">
                              {{ $t('AUDIT_LOGS.DETAILS.OLD_VALUE') }}
                            </th>
                            <th class="p-2 text-left font-semibold">
                              {{ $t('AUDIT_LOGS.DETAILS.NEW_VALUE') }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(change, field) in auditLogItem.audited_changes"
                            :key="field"
                            class="border-t border-n-weak"
                          >
                            <td class="p-2 font-medium">
                              {{ formatFieldName(field) }}
                            </td>
                            <template v-if="Array.isArray(change)">
                              <td class="p-2 text-red-600 dark:text-red-400">
                                {{ formatValue(change[0]) }}
                              </td>
                              <td class="p-2 text-green-600 dark:text-green-400">
                                {{ formatValue(change[1]) }}
                              </td>
                            </template>
                            <template v-else>
                              <td class="p-2">—</td>
                              <td class="p-2 text-green-600 dark:text-green-400">
                                {{ formatValue(change) }}
                              </td>
                            </template>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <TableFooter
          :current-page="Number(meta.currentPage)"
          :total-count="meta.totalEntries"
          :page-size="meta.perPage"
          class="border-n-weak border-t !px-0 py-4"
          @page-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>
